import { css, keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { Colors } from '../palettes/colorPalette';
import { backgroundTextureAlt } from '../uiConstants';

const keys = (color1: string, color2: string) => keyframes`
	0% {
		background: ${color1};
	}
	50% {
		background: ${color2};
	}
	100% {
		background: ${color1};
	}
`;

type ButtonProps = {
	flash?: boolean
	flashColors?: [string, string]
	reverse?: boolean,
	softDisable?: boolean
}

type ExtraMarketButtonProps = ButtonProps & {
	position: 'left' | 'center' | 'end' | 'round';
	disabled?: boolean;
}

export const Button = styled.button<ButtonProps>`
	background: linear-gradient(145deg, #f7ebda, #fffff4);
	border-radius: 50%;
	border: none;
	color: ${Colors.UITextAndBorders};
	height: 10vh;
	width: 10vh;
	padding: 0;
	position: relative;
	box-shadow:
		.2vh .2vh .8vh #fffff4,
		inset .2vh .2vh .8vh #fffff4,
		inset -.2vh -.2vh .8vh #918574;
	
	&:hover {
		cursor: cell;
	}

	&:active {
		box-shadow: 
			inset .2vh .2vh 1vh #c4b8a7,
			inset -.2vh -.2vh .8vh #fffff4;
	}

	img {
		max-width: 50%;
		max-height: 50%;
		object-fit: contain;
	}
	animation: ${props => props.flash ? css`${keys(props.flashColors![0], props.flashColors![1])} 1s infinite` : ''} 
`;

export const ExtraMarketButton = styled.button<ExtraMarketButtonProps>`
	background: url(${backgroundTextureAlt});
	background-color: ${Colors.UIMarketButton};
	border-top-left-radius: ${props => props.position === 'left' || props.position === 'round' ? "50%" : "0%"};
	border-bottom-left-radius: ${props => props.position === 'left' || props.position === 'round' ? "50%" : "0%"};
	border-top-right-radius: ${props => props.position === 'end' || props.position === 'round' ? "50%" : "0%"};
	border-bottom-right-radius: ${props => props.position === 'end' || props.position === 'round' ? "50%" : "0%"};
	border-color: ${Colors.UIMarketTextAndBorders};
	border: none;
	height: 8vh;
	width: 8vh;
	padding: 0;
	position: relative;
	box-shadow: 2px 2px 2px ${Colors.UIMarketShadow};
	
	&:hover {
		${props => !props.disabled ? 'cursor: cell;' : ''}
		${props => !props.disabled ? 'box-shadow: none' : ''}
	}

	${props => props.disabled && 'box-shadow: inset 2px 2px 2px ' + Colors.UIMarketShadow + '88;'}
	&:active {
		${props => !props.disabled ? 'box-shadow: inset 2px 2px 2px ' + Colors.UIMarketShadow + '88;' : ''}
	}

	img {
		max-width: 50%;
		max-height: 50%;
		object-fit: contain;
	}
	animation: ${props => props.flash ? css`${keys(props.flashColors![0], props.flashColors![1])} 1s infinite` : ''} 
`;

export const ImmuneCellButton = styled.button<ButtonProps>`
	background: ${props => props.softDisable === true ? Colors.UIButtonDisabled : Colors.UIAntibodyBackground};
	border-radius: 50%;
	border: none;
	height: 10vh;
	width: 10vh;
	padding: 0;
	position: relative;
	box-shadow: inset 3px 4px 2px 0px rgb(0 0 0 / 50%);
	
	&:hover {
		transform: scale(1.05);
		cursor: cell;
	}
	
	&:active {
		transform: scale(1.2);
	}

	img {
		max-width: 50%;
		max-height: 50%;
		object-fit: contain;
	}
	animation: ${props => props.flash ? css`${keys(props.flashColors![0], props.flashColors![1])} 1s infinite` : ''} 
`;

export const UIButton = styled.button<ButtonProps>`
    border: inset .5vw ${Colors.UITextAndBorders};
	color: ${Colors.UITextAndBorders};
    border-radius: 2vh;
	background: url(${backgroundTextureAlt});
	background-color: ${Colors.UIBackground};
	min-width: 20vw;
    max-width: 20vw;

	font-family: 'NotoSans';
	font-size: 3vw;

	&:disabled {
		pointer-events: none;
		background: ${Colors.UIUnemphasizedText};
	}

	&:hover {
		background-color: ${Colors.UIButtonHover};
		border: inset .5vw ${Colors.UIShadow};
	}
`;

export const UIButtonSmall = styled.button<ButtonProps>`
    border: inset .5vw ${Colors.UITextAndBorders};
    border-radius: 2vh;
	background: url(${backgroundTextureAlt});
	background-color: ${props => props.reverse ? Colors.UIButtonHover : Colors.UIBackground};
	color: ${Colors.UITextAndBorders};
	min-width: 10vw;
    max-width: 10vw;

	font-family: 'NotoSans';
	font-size: 1vw;

	&:disabled {
		background: #B8D4FF;
	}

	&:hover {
		background-color: ${props => props.reverse ? Colors.UIBackground : Colors.UIButtonHover};
    	border: inset .5vw ${Colors.UIShadow};
	}
`;

export const UIButtonThin = styled.button<ButtonProps>`
	margin-top: .5rem;
    border: inset .25vw ${Colors.UITextAndBorders};
    border-radius: 2vh;
	background: url(${backgroundTextureAlt});
	background-color: ${props => props.reverse ? Colors.UIButtonHover : Colors.UIBackground};
	color: ${Colors.UITextAndBorders};
	width: 100%;

	font-family: 'NotoSans';
	font-size: 1vw;

	&:disabled {
		background: #B8D4FF;
	}

	&:hover {
		background-color: ${props => props.reverse ? Colors.UIBackground : Colors.UIButtonHover};
    	border: inset .25vw ${Colors.UIShadow};
	}
`;